/**
 * @fileoverview ClubOS Finance Overview — FC Metropolitan ISL Season 2025/26
 * KPIs, AIFF FFP compliance, revenue breakdown, budget vs actuals, wage bill.
 * TODO: Replace mock data with API calls: financeService.getOverview()
 */

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftProgress from "components/SoftProgress";
import SoftBadge from "components/SoftBadge";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";

// ClubOS utilities & data
import { formatCompact, formatINR, SEASON, CLUB_NAME, LEAGUE } from "utils/indiaLocale";
import { financialSummary, squad } from "data/mockData";
import { PermissionGate } from "components/PermissionGate";
import { financeService } from "api";
import FootballLoader from "components/FootballLoader";

// ─── DERIVED DATA ──────────────────────────────────────────────────────────────

/** Budget vs Actuals chart data — values in Crores for readability */
const budgetVsActualChart = {
  labels: financialSummary.budgetVsActual.map((d) => d.month),
  datasets: [
    {
      label: "Budget",
      data: financialSummary.budgetVsActual.map((d) => parseFloat((d.budget / 1_00_00_000).toFixed(2))),
      color: "info",
    },
    {
      label: "Actual",
      data: financialSummary.budgetVsActual.map((d) => parseFloat((d.actual / 1_00_00_000).toFixed(2))),
      color: "dark",
    },
  ],
};

/** Top 5 earners sorted by monthly salary */
const topEarners = [...squad]
  .filter((p) => p.squad === "First Team")
  .sort((a, b) => b.monthlySalary - a.monthlySalary)
  .slice(0, 5);

// ─── COMPONENT ─────────────────────────────────────────────────────────────────

function FinanceOverview() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    financeService.getSummary()
      .then(setSummary)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Fall back to mock data while loading or if API fails
  const {
    totalMonthlySalaryBill,
    annualRevenue,
    transferBudget,
    transferBudgetUsed,
    ffpAllowable,
    ffpUsed,
    revenueBreakdown,
    wageBillBreakdown,
  } = financialSummary;

  const monthlySalaryBill = summary?.monthlyWageBill ?? totalMonthlySalaryBill;
  const apiRevenue        = summary ? (summary.totalRevenue) : annualRevenue;
  const netPos            = summary?.netPosition ?? (annualRevenue - totalMonthlySalaryBill * 12);

  // Computed percentages
  const ffpPct       = Math.round((ffpUsed / ffpAllowable) * 100);
  const ffpRemaining = ffpAllowable - ffpUsed;
  const budgetPct    = Math.round((transferBudgetUsed / transferBudget) * 100);
  const budgetLeft   = transferBudget - transferBudgetUsed;

  // FFP status
  const ffpStatus     = ffpPct > 90 ? "CRITICAL" : ffpPct > 60 ? "CAUTION" : "HEALTHY";
  const ffpStatusColor = ffpPct > 90 ? "error" : ffpPct > 60 ? "warning" : "success";
  const ffpBarColor    = ffpPct > 90 ? "error" : ffpPct > 60 ? "warning" : "success";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* ── Page header ─────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Financial Overview
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Season {SEASON} &middot; {CLUB_NAME} &middot; {LEAGUE}
          </SoftTypography>
        </SoftBox>

        {/* ── KPI Cards ───────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Monthly Wage Bill" }}
                count={loading ? "…" : formatCompact(monthlySalaryBill)}
                percentage={{ color: "error", text: "total salary" }}
                icon={{ color: "error", component: "account_balance_wallet" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Total Revenue" }}
                count={loading ? "…" : formatCompact(apiRevenue)}
                percentage={{ color: "success", text: summary ? "from DB" : "mock data" }}
                icon={{ color: "success", component: "trending_up" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "FFP Headroom" }}
                count={formatCompact(ffpRemaining)}
                percentage={{ color: ffpStatusColor, text: `${ffpPct}% of limit used` }}
                icon={{ color: ffpStatusColor, component: "policy" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Transfer Budget Remaining" }}
                count={formatCompact(budgetLeft)}
                percentage={{ color: "info", text: `${budgetPct}% deployed` }}
                icon={{ color: "info", component: "swap_horiz" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Revenue Breakdown + AIFF FFP ────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            {/* Revenue Breakdown */}
            <Grid item xs={12} lg={5}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={0.5}>
                    Revenue Breakdown
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mb={2}>
                    FY 2025-26 · Total: {formatCompact(annualRevenue)}
                  </SoftTypography>
                  {revenueBreakdown.map((item) => {
                    const pct = Math.round((item.value / annualRevenue) * 100);
                    return (
                      <SoftBox key={item.label} mb={1.5}>
                        <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                          <SoftTypography variant="caption" fontWeight="medium">
                            {item.label}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text">
                            {formatCompact(item.value)} ({pct}%)
                          </SoftTypography>
                        </SoftBox>
                        <SoftProgress value={pct} color={item.color || "info"} />
                      </SoftBox>
                    );
                  })}
                </SoftBox>
              </Card>
            </Grid>

            {/* AIFF Financial Fair Play */}
            <Grid item xs={12} lg={4}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={0.5}>
                    AIFF Financial Fair Play
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mb={2}>
                    3-season rolling calculation (2023/24–2025/26)
                  </SoftTypography>

                  <SoftBox mb={1.5}>
                    <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                      <SoftTypography variant="caption" fontWeight="medium">Allowable Loss</SoftTypography>
                      <SoftTypography variant="caption" color="text">
                        {formatCompact(ffpUsed)} / {formatCompact(ffpAllowable)}
                      </SoftTypography>
                    </SoftBox>
                    <SoftProgress value={ffpPct} color={ffpBarColor} label />
                  </SoftBox>

                  {/* Status banner */}
                  <SoftBox
                    mt={2}
                    p={1.5}
                    bgColor={ffpStatusColor}
                    variant="gradient"
                    borderRadius="md"
                  >
                    <SoftTypography variant="caption" color="white" fontWeight="bold">
                      {ffpStatus}:{" "}
                      {ffpPct > 90
                        ? "Approaching AIFF FFP limit — no further losses allowable"
                        : ffpPct > 60
                        ? "FFP headroom is limited — review expenditure before Jan window"
                        : "FFP position is healthy"}
                    </SoftTypography>
                  </SoftBox>

                  <Divider />
                  <SoftTypography variant="caption" color="text" display="block" mt={1}>
                    Headroom remaining:{" "}
                    <strong>{formatCompact(ffpRemaining)}</strong>
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block">
                    Used: <strong>{formatINR(ffpUsed)}</strong> of{" "}
                    <strong>{formatINR(ffpAllowable)}</strong> allowable
                  </SoftTypography>
                </SoftBox>
              </Card>
            </Grid>

            {/* Transfer Budget */}
            <Grid item xs={12} lg={3}>
              <Card sx={{ height: "100%" }}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={0.5}>
                    Transfer Budget
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mb={2}>
                    Season {SEASON} allocation
                  </SoftTypography>

                  <SoftBox mb={1}>
                    <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                      <SoftTypography variant="caption" fontWeight="medium">Deployed</SoftTypography>
                      <SoftTypography variant="caption" color="text">{budgetPct}%</SoftTypography>
                    </SoftBox>
                    <SoftProgress value={budgetPct} color="info" />
                  </SoftBox>

                  <Divider />
                  <SoftBox mt={1}>
                    <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                      <SoftTypography variant="caption" color="text">Total Budget</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="medium">{formatCompact(transferBudget)}</SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                      <SoftTypography variant="caption" color="text">Spent</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="medium" color="error">{formatCompact(transferBudgetUsed)}</SoftTypography>
                    </SoftBox>
                    <SoftBox display="flex" justifyContent="space-between">
                      <SoftTypography variant="caption" color="text">Remaining</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold" color="success">{formatCompact(budgetLeft)}</SoftTypography>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Budget vs Actuals Chart ──────────────────────────────────────── */}
        <SoftBox mb={3}>
          <VerticalBarChart
            title="Budget vs Actuals (₹ Crores)"
            description="Monthly expenditure — ISL season Sep–Mar"
            chart={budgetVsActualChart}
            height="16rem"
          />
        </SoftBox>

        {/* ── Wage Bill Breakdown + Top Earners ───────────────────────────── */}
        <Grid container spacing={3}>
          {/* Wage Breakdown by Department */}
          <Grid item xs={12} lg={5}>
            <Card>
              <SoftBox p={3}>
                <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                  Wage Bill Breakdown
                </SoftTypography>
                <SoftTypography variant="caption" color="text" display="block" mb={2}>
                  Monthly total: {formatCompact(totalMonthlySalaryBill)}
                </SoftTypography>
                {wageBillBreakdown.map((item) => {
                  const pct = Math.round((item.value / totalMonthlySalaryBill) * 100);
                  return (
                    <SoftBox key={item.label} mb={1.5}>
                      <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                        <SoftTypography variant="caption" fontWeight="medium">{item.label}</SoftTypography>
                        <SoftTypography variant="caption" color="text">
                          {formatCompact(item.value)} ({pct}%)
                        </SoftTypography>
                      </SoftBox>
                      <SoftProgress value={pct} color="info" />
                    </SoftBox>
                  );
                })}
              </SoftBox>
            </Card>
          </Grid>

          {/* Top Earners — salary hidden behind permission gate */}
          <Grid item xs={12} lg={7}>
            <Card>
              <SoftBox p={3}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <SoftTypography variant="h6" fontWeight="medium">
                    Top Earners — Monthly Salary
                  </SoftTypography>
                  <SoftBadge badgeContent="Finance Only" color="warning" size="xs" container />
                </SoftBox>

                {loading ? (
                  <FootballLoader label="Loading overview…" />
                ) : (
                  <SoftBox
                    component="table"
                    sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}
                  >
                    <SoftBox component="thead">
                      <SoftBox component="tr">
                        {["Player", "Position", "Nationality", "Monthly Salary"].map((h) => (
                          <SoftBox
                            key={h}
                            component="th"
                            sx={{
                              textAlign: "left",
                              pb: 1,
                              pr: 2,
                              color: "text.secondary",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              textTransform: "uppercase",
                              borderBottom: "2px solid #e9ecef",
                            }}
                          >
                            {h}
                          </SoftBox>
                        ))}
                      </SoftBox>
                    </SoftBox>
                    <SoftBox component="tbody">
                      {topEarners.map((p) => (
                        <SoftBox key={p.id} component="tr" sx={{ borderTop: "1px solid #f0f0f0" }}>
                          <SoftBox component="td" sx={{ py: 1, pr: 2 }}>
                            <SoftTypography variant="button" fontWeight="medium">{p.name}</SoftTypography>
                          </SoftBox>
                          <SoftBox component="td" sx={{ py: 1, pr: 2 }}>
                            <SoftBadge badgeContent={p.position} color="info" size="xs" container />
                          </SoftBox>
                          <SoftBox component="td" sx={{ py: 1, pr: 2 }}>
                            <SoftTypography variant="caption" color="text">
                              {p.flag} {p.nationality}
                            </SoftTypography>
                          </SoftBox>
                          <SoftBox component="td" sx={{ py: 1 }}>
                            {/* Salary is sensitive — hidden without finance.salary.view */}
                            <PermissionGate
                              permission="finance.salary.view"
                              fallback={
                                <Tooltip title="Finance access required to view salaries">
                                  <SoftTypography variant="button" fontWeight="bold" color="secondary">
                                    ₹ ***
                                  </SoftTypography>
                                </Tooltip>
                              }
                            >
                              <SoftTypography variant="button" fontWeight="bold" color="dark">
                                {formatCompact(p.monthlySalary)}
                              </SoftTypography>
                            </PermissionGate>
                          </SoftBox>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  </SoftBox>
                )}

                <SoftTypography variant="caption" color="secondary" display="block" mt={1}>
                  * Monthly salaries visible to Finance roles (CFO, Finance Admin) only.
                </SoftTypography>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />
    </DashboardLayout>
  );
}

export default FinanceOverview;
